import { openDB } from "idb";

const DB_NAME = "image-cache";
const STORE = "images";
const MAX_ENTRIES = 300;

let dbPromise = null;
const getDb = () => {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, 1, {
      upgrade(db) {
        db.createObjectStore(STORE, { keyPath: "key" });
      },
    }).catch((err) => {
      dbPromise = null; // allow retry later
      throw err;
    });
  }
  return dbPromise;
};

// Only remote http(s) URLs from a different origin are cacheable.
// Local imports (/assets/...), data: and blob: URLs are skipped.
export const getCacheKey = (src) => {
  if (typeof src !== "string") return null;
  try {
    const url = new URL(src);
    if (url.protocol !== "https:" && url.protocol !== "http:") return null;
    if (url.origin === window.location.origin) return null;
    // Stable across presigned signatures: host + path, no query string.
    return `${url.host}${url.pathname}`;
  } catch {
    return null;
  }
};

// key -> { objectUrl, refs }   (session-level layer; makes remounts instant)
const memory = new Map();
// key -> Promise<Blob | null>  (de-dupes concurrent lookups/fetches)
const inFlight = new Map();

const evictIfNeeded = async (db) => {
  const count = await db.count(STORE);
  if (count <= MAX_ENTRIES) return;
  const all = await db.getAll(STORE);
  all.sort((a, b) => a.lastAccess - b.lastAccess);
  const tx = db.transaction(STORE, "readwrite");
  all.slice(0, count - MAX_ENTRIES).forEach((e) => tx.store.delete(e.key));
  await tx.done;
};

const loadBlob = async (key, src, signal) => {
  let db = null;
  try {
    db = await getDb();
    const hit = await db.get(STORE, key);
    if (hit?.blob) {
      db.put(STORE, { ...hit, lastAccess: Date.now() }).catch(() => {});
      return hit.blob;
    }
  } catch {
    // IndexedDB unavailable (private mode etc.) -> continue to network
  }

  const res = await fetch(src, { signal });
  const type = res.headers.get("content-type") || "";
  // Never cache S3 XML error bodies (e.g. expired signature -> 403)
  if (!res.ok || !type.startsWith("image/")) return null;
  const blob = await res.blob();

  if (db) {
    db.put(STORE, { key, blob, lastAccess: Date.now() })
      .then(() => evictIfNeeded(db))
      .catch(() => {}); // quota exceeded etc. -> still show the image
  }
  return blob;
};

// Synchronous: returns an object URL if this session already has it.
export const getFromMemory = (key) => memory.get(key)?.objectUrl || null;

// Async: resolves to an object URL, or null if it can't be cached.
export const acquireImage = async (key, src, signal) => {
  const existing = memory.get(key);
  if (existing) {
    existing.refs += 1;
    return existing.objectUrl;
  }

  let pending = inFlight.get(key);
  if (!pending) {
    // The shared request must not be aborted by one consumer unmounting.
    pending = loadBlob(key, src).finally(() => inFlight.delete(key));
    inFlight.set(key, pending);
  }

  const blob = await pending;
  if (!blob || signal?.aborted) return null;

  const entry = memory.get(key);
  if (entry) {
    entry.refs += 1;
    return entry.objectUrl;
  }
  const objectUrl = URL.createObjectURL(blob);
  memory.set(key, { objectUrl, refs: 1 });
  return objectUrl;
};

export const releaseImage = (key) => {
  const entry = memory.get(key);
  if (!entry) return;
  entry.refs -= 1;
  // Keep the object URL alive for the session for instant remounts;
  // it is small (a pointer to the blob) and freed on page unload.
};

export const clearImageCache = async () => {
  memory.forEach(({ objectUrl }) => URL.revokeObjectURL(objectUrl));
  memory.clear();
  try {
    const db = await getDb();
    await db.clear(STORE);
  } catch {
    /* ignore */
  }
};
